// client/src/components/admin/AdminAvailabilityView.js
import React, { useEffect, useState, useMemo, useCallback } from 'react'
import PageLayout from '../common/layout/PageLayout'
import useAuth from '../../hooks/useAuth'
import ApiAdminTools from '../../api/ApiAdminTools'
import ApiAvailability from '../../api/ApiAvailability'
import ApiLeagues from '../../api/ApiLeagues'
import toastr from 'toastr'
import './AdminAvailabilityView.css'

function formatDateTime(dateTimeStr) {
  if (!dateTimeStr) return '—'
  const date = new Date(dateTimeStr)
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

function getRecurringBadgeClass(isRecurring) {
  return isRecurring ? 'badge bg-success' : 'badge bg-secondary'
}

function AdminAvailabilityView() {
  const { isAdmin, loading: authLoading } = useAuth()
  const [availabilities, setAvailabilities] = useState([])
  const [totalAvailabilities, setTotalAvailabilities] = useState(0)
  const [leagues, setLeagues] = useState([])
  const [divisions, setDivisions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [leagueError, setLeagueError] = useState(null)
  const [lastRefresh, setLastRefresh] = useState(null)

  // Filters
  const [dateRangeFilter, setDateRangeFilter] = useState('future') // 'future', 'next7', 'next30', 'all'
  const [showPastAvailability, setShowPastAvailability] = useState(false)
  const [leagueFilter, setLeagueFilter] = useState('all')
  const [divisionFilter, setDivisionFilter] = useState('all')
  const [recurringFilter, setRecurringFilter] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')

  // Sorting
  const [sortColumn, setSortColumn] = useState('user_lastname')
  const [sortDirection, setSortDirection] = useState('asc')

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 50

  // Player grouping state
  const [expandedPlayers, setExpandedPlayers] = useState({})
  const [expandAll, setExpandAll] = useState(false)

  // Fetch leagues for filter dropdown
  useEffect(() => {
    async function fetchLeagues() {
      try {
        const data = await ApiLeagues.getAll()
        setLeagues(data || [])
        setLeagueError(null)
      } catch (err) {
        console.error('Failed to fetch leagues:', err)
        setLeagueError('Failed to load leagues for filter')
      }
    }
    if (isAdmin) {
      fetchLeagues()
    }
  }, [isAdmin])

  // Fetch divisions when league changes
  useEffect(() => {
    async function fetchDivisions() {
      if (leagueFilter === 'all') {
        setDivisions([])
        setDivisionFilter('all')
        return
      }

      try {
        const selectedLeague = leagues.find(
          (l) => l.id === parseInt(leagueFilter, 10),
        )
        if (selectedLeague?.divisions) {
          setDivisions(selectedLeague.divisions)
        }
      } catch (err) {
        console.error('Failed to fetch divisions:', err)
      }
    }
    fetchDivisions()
  }, [leagueFilter, leagues])

  // Fetch availabilities with server-side pagination and sorting
  const fetchAvailabilities = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const options = {
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage,
        sortBy: sortColumn,
        sortDirection: sortDirection,
      }

      // Add league filter if selected
      if (leagueFilter !== 'all') {
        options.leagueId = parseInt(leagueFilter, 10)
      }

      // Add division filter if selected
      if (divisionFilter !== 'all') {
        options.divisionId = parseInt(divisionFilter, 10)
      }

      // Add recurring filter if selected
      if (recurringFilter !== 'all') {
        options.isRecurring = recurringFilter === 'recurring'
      }

      // Add date range filter
      if (dateRangeFilter === 'future' && !showPastAvailability) {
        const now = new Date()
        options.startDate = now.toISOString()
      } else if (dateRangeFilter === 'next7') {
        const now = new Date()
        const next7 = new Date()
        next7.setDate(now.getDate() + 7)
        options.startDate = now.toISOString()
        options.endDate = next7.toISOString()
      } else if (dateRangeFilter === 'next30') {
        const now = new Date()
        const next30 = new Date()
        next30.setDate(now.getDate() + 30)
        options.startDate = now.toISOString()
        options.endDate = next30.toISOString()
      }

      // Add search query
      if (searchQuery.trim()) {
        options.search = searchQuery.trim()
      }

      const response = await ApiAdminTools.getAllAvailability(options)
      setAvailabilities(response.availabilities || [])
      setTotalAvailabilities(response.total || 0)
      setLastRefresh(new Date())
    } catch (err) {
      console.error('Failed to fetch availabilities:', err)
      setError('Failed to load availabilities. Please try again.')
      toastr.error('Failed to load availabilities')
    } finally {
      setLoading(false)
    }
  }, [
    currentPage,
    dateRangeFilter,
    leagueFilter,
    divisionFilter,
    recurringFilter,
    searchQuery,
    showPastAvailability,
    sortColumn,
    sortDirection,
  ])

  useEffect(() => {
    if (isAdmin && !authLoading) {
      fetchAvailabilities()
    }
  }, [isAdmin, authLoading, fetchAvailabilities])

  // Group availabilities by player
  const groupedAvailabilities = useMemo(() => {
    const grouped = {}
    availabilities.forEach((availability) => {
      const key = availability.user_id
      if (!grouped[key]) {
        grouped[key] = {
          user_id: availability.user_id,
          firstname: availability.firstname,
          lastname: availability.lastname,
          email: availability.email,
          availabilities: [],
        }
      }
      grouped[key].availabilities.push(availability)
    })
    return Object.values(grouped)
  }, [availabilities])

  const totalPages = Math.ceil(totalAvailabilities / itemsPerPage)

  // Handle column sort
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
    setCurrentPage(1) // Reset to first page when sorting changes
  }

  // Get ARIA sort attribute for column
  const getAriaSortValue = (column) => {
    if (sortColumn !== column) return 'none'
    return sortDirection === 'asc' ? 'ascending' : 'descending'
  }

  const getSortIcon = (column) => {
    if (sortColumn !== column) return null
    return sortDirection === 'asc' ? '▲' : '▼'
  }

  // Helper for sortable header props
  const getSortableHeaderProps = (column) => ({
    className: 'admin-availability-sortable-header',
    onClick: () => handleSort(column),
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        handleSort(column)
      }
    },
    role: 'button',
    tabIndex: 0,
    'aria-sort': getAriaSortValue(column),
  })

  // Toggle player expand/collapse
  const togglePlayer = (userId) => {
    setExpandedPlayers((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }))
  }

  // Expand/Collapse all players
  const handleExpandAll = () => {
    const newExpandAll = !expandAll
    setExpandAll(newExpandAll)
    const newExpanded = {}
    groupedAvailabilities.forEach((player) => {
      newExpanded[player.user_id] = newExpandAll
    })
    setExpandedPlayers(newExpanded)
  }

  // Handle delete availability
  const handleDelete = async (availabilityId) => {
    if (
      !window.confirm(
        'Are you sure you want to delete this availability entry?',
      )
    ) {
      return
    }

    try {
      await ApiAvailability.deleteAvailability(availabilityId)
      toastr.success('Availability deleted successfully')
      fetchAvailabilities() // Refresh the list
    } catch (err) {
      console.error('Failed to delete availability:', err)
      toastr.error('Failed to delete availability')
    }
  }

  // Determine empty state message
  const emptyStateMessage = useMemo(() => {
    if (
      searchQuery ||
      leagueFilter !== 'all' ||
      divisionFilter !== 'all' ||
      recurringFilter !== 'all'
    ) {
      return 'No availability entries found matching your filters. Try adjusting your search criteria.'
    }
    return 'There are no availability entries in the system.'
  }, [searchQuery, leagueFilter, divisionFilter, recurringFilter])

  // Generate pagination page numbers
  const paginationPages = useMemo(() => {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }, [totalPages])

  if (authLoading) {
    return (
      <PageLayout title="Admin Availability">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </PageLayout>
    )
  }

  if (!isAdmin) {
    return (
      <PageLayout title="Admin Availability">
        <div className="alert alert-danger">
          You do not have permission to view this page.
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout title="Player Availability">
      <div className="admin-availability-container">
        {/* Header with filters */}
        <div className="admin-availability-header">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4">
            <div>
              <h4 className="mb-1">Player Availability</h4>
              <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                {totalAvailabilities} availability entr
                {totalAvailabilities !== 1 ? 'ies' : 'y'} found
                {lastRefresh && (
                  <span className="ms-2">
                    • Last updated {lastRefresh.toLocaleTimeString()}
                  </span>
                )}
              </p>
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={handleExpandAll}
                data-testid="expand-collapse-all-button"
              >
                <i
                  className={`bi bi-${expandAll ? 'dash' : 'plus'}-square me-1`}
                ></i>
                {expandAll ? 'Collapse All' : 'Expand All'}
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={fetchAvailabilities}
                disabled={loading}
                data-testid="refresh-button"
              >
                <i className="bi bi-arrow-clockwise me-1"></i>
                Refresh
              </button>
            </div>
          </div>

          {/* Filters bar */}
          <div className="admin-availability-filters">
            <div className="row g-3 mb-4">
              {/* Date range filter */}
              <div className="col-12 col-md-6 col-lg-3">
                <label
                  htmlFor="dateRangeFilter"
                  className="form-label"
                  style={{ fontSize: '0.875rem', fontWeight: 500 }}
                >
                  Date Range
                </label>
                <select
                  id="dateRangeFilter"
                  className="form-select form-select-sm"
                  value={dateRangeFilter}
                  onChange={(e) => {
                    setDateRangeFilter(e.target.value)
                    setCurrentPage(1)
                  }}
                  data-testid="date-range-filter"
                >
                  {/* Options ordered to match new default (future-only) */}
                  <option value="future">Future Only</option>
                  <option value="all">All</option>
                  <option value="next7">Next 7 Days</option>
                  <option value="next30">Next 30 Days</option>
                </select>
              </div>

              {/* League filter */}
              <div className="col-12 col-md-6 col-lg-2">
                <label
                  htmlFor="leagueFilter"
                  className="form-label"
                  style={{ fontSize: '0.875rem', fontWeight: 500 }}
                >
                  League
                </label>
                <select
                  id="leagueFilter"
                  className="form-select form-select-sm"
                  value={leagueFilter}
                  onChange={(e) => {
                    setLeagueFilter(e.target.value)
                    setCurrentPage(1)
                  }}
                  data-testid="league-filter"
                >
                  <option value="all">All Leagues</option>
                  {leagues.map((league) => (
                    <option key={league.id} value={league.id}>
                      {league.name}
                    </option>
                  ))}
                </select>
                {leagueError && (
                  <small className="text-warning d-block mt-1">
                    ⚠️ {leagueError}
                  </small>
                )}
              </div>

              {/* Division filter */}
              <div className="col-12 col-md-6 col-lg-2">
                <label
                  htmlFor="divisionFilter"
                  className="form-label"
                  style={{ fontSize: '0.875rem', fontWeight: 500 }}
                >
                  Division
                </label>
                <select
                  id="divisionFilter"
                  className="form-select form-select-sm"
                  value={divisionFilter}
                  onChange={(e) => {
                    setDivisionFilter(e.target.value)
                    setCurrentPage(1)
                  }}
                  disabled={leagueFilter === 'all' || divisions.length === 0}
                  data-testid="division-filter"
                >
                  <option value="all">All Divisions</option>
                  {divisions.map((division) => (
                    <option key={division.id} value={division.id}>
                      {division.division_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Recurring filter */}
              <div className="col-12 col-md-6 col-lg-2">
                <label
                  htmlFor="recurringFilter"
                  className="form-label"
                  style={{ fontSize: '0.875rem', fontWeight: 500 }}
                >
                  Recurring
                </label>
                <select
                  id="recurringFilter"
                  className="form-select form-select-sm"
                  value={recurringFilter}
                  onChange={(e) => {
                    setRecurringFilter(e.target.value)
                    setCurrentPage(1)
                  }}
                  data-testid="recurring-filter"
                >
                  <option value="all">All</option>
                  <option value="recurring">Recurring Only</option>
                  <option value="onetime">One-time Only</option>
                </select>
              </div>

              {/* Search box */}
              <div className="col-12 col-md-6 col-lg-3">
                <label
                  htmlFor="searchQuery"
                  className="form-label"
                  style={{ fontSize: '0.875rem', fontWeight: 500 }}
                >
                  Search
                </label>
                <input
                  id="searchQuery"
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Name, email, league..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value)
                    setCurrentPage(1)
                  }}
                  data-testid="search-input"
                />
              </div>
            </div>
            {/* Show past availability */}
            <div className="col-12">
              <div className="form-check">
                <input
                  id="showPastAvailability"
                  type="checkbox"
                  className="form-check-input"
                  checked={showPastAvailability}
                  onChange={(e) => {
                    setShowPastAvailability(e.target.checked)
                    setCurrentPage(1)
                  }}
                  data-testid="show-past-availability-checkbox"
                />
                <label
                  htmlFor="showPastAvailability"
                  className="form-check-label"
                  style={{ fontSize: '0.875rem' }}
                >
                  Show past availability
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading availabilities...</span>
            </div>
          </div>
        )}

        {/* Error state */}
        {error && !loading && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && availabilities.length === 0 && (
          <div className="admin-availability-empty-state">
            <i className="bi bi-calendar-x"></i>
            <h5>No Availability Found</h5>
            <p className="text-muted">{emptyStateMessage}</p>
          </div>
        )}

        {/* Desktop Table View */}
        {!loading && !error && availabilities.length > 0 && (
          <>
            <div className="admin-availability-table-container d-none d-lg-block">
              {groupedAvailabilities.map((player) => {
                const isExpanded = expandedPlayers[player.user_id]
                return (
                  <div
                    key={player.user_id}
                    className="admin-availability-player-group"
                  >
                    {/* Player header row */}
                    <div
                      className="admin-availability-player-header"
                      onClick={() => togglePlayer(player.user_id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          togglePlayer(player.user_id)
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-expanded={isExpanded}
                      data-testid={`player-header-${player.user_id}`}
                    >
                      <i
                        className={`bi bi-chevron-${isExpanded ? 'down' : 'right'} me-2`}
                        aria-hidden="true"
                      ></i>
                      <strong>
                        {player.firstname} {player.lastname}
                      </strong>
                      <span className="text-muted ms-2">({player.email})</span>
                      <span className="badge bg-primary ms-2">
                        {player.availabilities.length} slot
                        {player.availabilities.length !== 1 ? 's' : ''}
                      </span>
                    </div>

                    {/* Player's availability table */}
                    {isExpanded && (
                      <table className="admin-availability-table">
                        <thead>
                          <tr>
                            <th
                              {...getSortableHeaderProps('start_time')}
                              style={{ width: '140px' }}
                            >
                              Start Time
                              <span className="admin-availability-sort-indicator">
                                {getSortIcon('start_time')}
                              </span>
                            </th>
                            <th style={{ width: '140px' }}>End Time</th>
                            <th
                              {...getSortableHeaderProps('league_name')}
                              style={{ width: '150px' }}
                            >
                              League
                              <span className="admin-availability-sort-indicator">
                                {getSortIcon('league_name')}
                              </span>
                            </th>
                            <th
                              {...getSortableHeaderProps('division_name')}
                              style={{ width: '120px' }}
                            >
                              Division
                              <span className="admin-availability-sort-indicator">
                                {getSortIcon('division_name')}
                              </span>
                            </th>
                            <th style={{ width: '100px' }}>Recurring</th>
                            <th style={{ width: '120px' }}>Rule</th>
                            <th style={{ width: '80px' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {player.availabilities.map((availability) => (
                            <tr key={availability.id}>
                              <td className="admin-availability-cell-datetime">
                                {formatDateTime(availability.start_time)}
                              </td>
                              <td className="admin-availability-cell-datetime">
                                {formatDateTime(availability.end_time)}
                              </td>
                              <td
                                className="admin-availability-cell-league"
                                title={availability.league_name}
                              >
                                {availability.league_name || '—'}
                              </td>
                              <td className="admin-availability-cell-division">
                                {availability.division_name || '—'}
                              </td>
                              <td className="admin-availability-cell-recurring">
                                <span
                                  className={getRecurringBadgeClass(
                                    availability.is_recurring,
                                  )}
                                >
                                  {availability.is_recurring ? 'Yes' : 'No'}
                                </span>
                              </td>
                              <td className="admin-availability-cell-rule">
                                {availability.recurrence_rule || '—'}
                              </td>
                              <td className="admin-availability-cell-actions">
                                <div className="d-flex gap-1">
                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() =>
                                      handleDelete(availability.id)
                                    }
                                    title="Delete availability"
                                    aria-label="Delete availability"
                                    data-testid={`delete-availability-${availability.id}`}
                                  >
                                    <i
                                      className="bi bi-trash"
                                      aria-hidden="true"
                                    ></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Mobile Card View */}
            <div className="admin-availability-mobile-view d-lg-none">
              {groupedAvailabilities.map((player) => {
                const isExpanded = expandedPlayers[player.user_id]
                return (
                  <div
                    key={player.user_id}
                    className="admin-availability-mobile-card card mb-3"
                  >
                    <div className="card-body">
                      <div
                        className="d-flex justify-content-between align-items-start mb-2"
                        onClick={() => togglePlayer(player.user_id)}
                        role="button"
                        tabIndex={0}
                        aria-expanded={isExpanded}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            togglePlayer(player.user_id)
                          }
                        }}
                      >
                        <div>
                          <h6 className="mb-1">
                            <i
                              className={`bi bi-chevron-${isExpanded ? 'down' : 'right'} me-2`}
                              aria-hidden="true"
                            ></i>
                            {player.firstname} {player.lastname}
                          </h6>
                          <small className="text-muted">{player.email}</small>
                        </div>
                        <span className="badge bg-primary">
                          {player.availabilities.length}
                        </span>
                      </div>

                      {isExpanded && (
                        <div className="mt-3">
                          {player.availabilities.map((availability, index) => (
                            <div
                              key={availability.id}
                              className={`availability-item ${index > 0 ? 'mt-3 pt-3 border-top' : ''}`}
                            >
                              <div style={{ fontSize: '0.875rem' }}>
                                <div>
                                  <strong>Start:</strong>{' '}
                                  {formatDateTime(availability.start_time)}
                                </div>
                                <div>
                                  <strong>End:</strong>{' '}
                                  {formatDateTime(availability.end_time)}
                                </div>
                                <div>
                                  <strong>League:</strong>{' '}
                                  {availability.league_name || '—'}
                                </div>
                                <div>
                                  <strong>Division:</strong>{' '}
                                  {availability.division_name || '—'}
                                </div>
                                <div>
                                  <strong>Recurring:</strong>{' '}
                                  <span
                                    className={getRecurringBadgeClass(
                                      availability.is_recurring,
                                    )}
                                  >
                                    {availability.is_recurring ? 'Yes' : 'No'}
                                  </span>
                                </div>
                                {availability.recurrence_rule && (
                                  <div>
                                    <strong>Rule:</strong>{' '}
                                    {availability.recurrence_rule}
                                  </div>
                                )}
                              </div>
                              <button
                                className="btn btn-sm btn-outline-danger mt-2"
                                onClick={() => handleDelete(availability.id)}
                                data-testid={`delete-availability-mobile-${availability.id}`}
                              >
                                <i className="bi bi-trash me-1"></i>
                                Delete
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="admin-availability-pagination">
                <nav aria-label="Availability pagination">
                  <ul className="pagination pagination-sm justify-content-center mb-0">
                    <li
                      className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        aria-label="Previous page"
                        data-testid="pagination-previous"
                      >
                        Previous
                      </button>
                    </li>
                    {paginationPages.map((pageNum) => {
                      // Show first, last, current, and pages around current
                      if (
                        pageNum === 1 ||
                        pageNum === totalPages ||
                        (pageNum >= currentPage - 1 &&
                          pageNum <= currentPage + 1)
                      ) {
                        return (
                          <li
                            key={pageNum}
                            className={`page-item ${currentPage === pageNum ? 'active' : ''}`}
                          >
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(pageNum)}
                              data-testid={`pagination-page-${pageNum}`}
                            >
                              {pageNum}
                            </button>
                          </li>
                        )
                      } else if (
                        pageNum === currentPage - 2 ||
                        pageNum === currentPage + 2
                      ) {
                        return (
                          <li key={pageNum} className="page-item disabled">
                            <span className="page-link">...</span>
                          </li>
                        )
                      }
                      return null
                    })}
                    <li
                      className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        aria-label="Next page"
                        data-testid="pagination-next"
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
                <p
                  className="text-center text-muted mt-2 mb-0"
                  style={{ fontSize: '0.875rem' }}
                >
                  Page {currentPage} of {totalPages} ({totalAvailabilities}{' '}
                  total entries)
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </PageLayout>
  )
}

export default AdminAvailabilityView
